import { type } from 'arktype';

/**
 * Weekday assignment map: employeeId → shiftUnitId[]
 * Each employee can have multiple shift units assigned per weekday.
 *
 * @example
 * {
 *   "employee-uuid-123": ["unit-uuid-456", "unit-uuid-789"],
 *   "employee-uuid-456": ["unit-uuid-012"]
 * }
 */
export const weekdayAssignmentsSchema = type({
  '[string]': 'string[]>=1', // employeeId → shiftUnitId[]
}).configure({
  description:
    'Per-weekday assignment map. Keys are employee IDs, values are non-empty arrays of shift unit IDs assigned to that employee on the weekday.',
});

export type WeekdayAssignments = Record<string, string[]>;

/**
 * Type-safe schedule structure mapping dates to assignments.
 *
 * Lives here (next to {@link WeekdayAssignments}, its only dependency) rather
 * than in `expanded-schedule.model` so that lower-level modules such as the
 * conflict types can reference it without creating an import cycle.
 */
export type ScheduleMap = Record<string, WeekdayAssignments>;

/**
 * Plan version data structure (weekday-based, supports multi-week).
 *
 * Legacy keys: "weekday:N" where N is 0-6 (Monday-Sunday).
 * Multi-week keys: "w{weekIndex}:weekday:{isoDay}" (e.g. "w0:weekday:0", "w1:weekday:4").
 *
 * @example
 * {
 *   "weekday:0": { "emp-1": ["unit-1", "unit-2"] },
 *   "w1:weekday:0": { "emp-1": ["unit-3"] },
 *   "__meta": { "templateSpanWeeks": 2 }
 * }
 */
export const planVersionDataSchema = type({
  '[/^(weekday:[0-6]|w[0-3]:weekday:[0-6])$/]': weekdayAssignmentsSchema,
}).configure({
  description:
    'Plan version assignment data. Keys follow the legacy `weekday:N` (N=0–6, Mon–Sun) or multi-week `wI:weekday:N` (I=0–3) format. Values are employee→shift-unit maps. An optional `__meta` key carries template metadata such as `templateSpanWeeks`.',
});

/**
 * Strict type inferred from arktype schema.
 * Use PlanVersionData for runtime use where indexing is needed.
 */
export type PlanVersionDataStrict = typeof planVersionDataSchema.t;

/** Matches legacy "weekday:N" and multi-week "w{index}:weekday:N" formats */
const DAY_KEY_PATTERN = /^(weekday:[0-6]|w[0-3]:weekday:[0-6])$/;

/** Known metadata keys stored alongside weekday assignments in version data.
 *  Includes legacy `shiftIds` for backward compat with versions created before the rename.
 *  `__meta` stores template metadata such as `templateSpanWeeks` and — for the
 *  plan template editor — a `defaultSuppressions` map (weekdayKey → employeeId[])
 *  used to render person defaults as suppressed chips per weekday. */
export const KNOWN_METADATA_KEYS = new Set(['shiftUnitIds', 'shiftIds', '__meta']);

// ---------------------------------------------------------------------------
// Day-key parsing and normalization
// ---------------------------------------------------------------------------

/** Allowed values for `__meta.templateSpanWeeks` */
export type TemplateSpanWeeks = 1 | 2 | 3 | 4;

/**
 * Per-weekday capacity overrides stored in `__meta.capacityOverrides`.
 * Maps dayKey → shiftUnitId → overridden capacity.
 *
 * Only **changed** values are stored. If a shift unit's capacity for a weekday
 * matches the default (from `shift.rules.capacity`), it is omitted.
 * An empty object (or absent key) means "use default capacity everywhere".
 * A value of `0` means zero capacity for that shift unit on that day.
 *
 * @example
 * {
 *   "weekday:0": { "unit-1": 5 },
 *   "w1:weekday:3": { "unit-1": 2, "unit-2": 0 }
 * }
 */
export type TemplateCapacityOverrides = Record<string, Record<string, number>>;

/** Parsed representation of a day key */
export interface ParsedDayKey {
  weekIndex: number;
  isoDay: number;
}

/**
 * Parses a day key string into its components.
 *
 * Accepted formats:
 * - `"weekday:N"` → `{ weekIndex: 0, isoDay: N }`
 * - `"w{W}:weekday:{D}"` → `{ weekIndex: W, isoDay: D }`
 *
 * Returns `null` for unrecognised formats.
 */
export function parseDayKey(key: string): ParsedDayKey | null {
  const multiWeekMatch = /^w([0-3]):weekday:([0-6])$/.exec(key);
  if (multiWeekMatch) {
    return { weekIndex: Number(multiWeekMatch[1]), isoDay: Number(multiWeekMatch[2]) };
  }

  const legacyMatch = /^weekday:([0-6])$/.exec(key);
  if (legacyMatch) {
    return { weekIndex: 0, isoDay: Number(legacyMatch[1]) };
  }

  return null;
}

/**
 * Formats a parsed day key back to its canonical multi-week string form.
 */
export function formatDayKey(weekIndex: number, isoDay: number): string {
  return `w${weekIndex}:weekday:${isoDay}`;
}

/**
 * Normalizes a day key to the canonical multi-week format.
 * Legacy `"weekday:N"` becomes `"w0:weekday:N"`.
 * Already-qualified keys are returned as-is.
 *
 * Returns `null` for unrecognised formats.
 */
export function normalizeDayKey(key: string): string | null {
  const parsed = parseDayKey(key);
  if (!parsed) return null;
  return formatDayKey(parsed.weekIndex, parsed.isoDay);
}

/**
 * Returns true if the given key is a valid day key (legacy or multi-week).
 */
export function isDayKey(key: string): boolean {
  return DAY_KEY_PATTERN.test(key);
}

/**
 * Extracts `__meta.templateSpanWeeks` from version data.
 * Returns `1` when absent (backward-compatible default).
 */
export function getTemplateSpanWeeks(data: Record<string, unknown>): TemplateSpanWeeks {
  const meta = data['__meta'];
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const metaRecord = meta as Record<string, unknown>;
    if ('templateSpanWeeks' in metaRecord) {
      const span = metaRecord['templateSpanWeeks'];
      if (span === 1 || span === 2 || span === 3 || span === 4) return span;
    }
  }

  // Backward-compatible fallback:
  // infer the span from existing day keys when __meta is missing.
  // This avoids rendering empty grids for historic versions that contain
  // multi-week keys but no explicit templateSpanWeeks metadata.
  let maxWeekIndex = 0;
  for (const key of Object.keys(data)) {
    const parsed = parseDayKey(key);
    if (!parsed) continue;
    if (parsed.weekIndex > maxWeekIndex) {
      maxWeekIndex = parsed.weekIndex;
    }
  }
  if (maxWeekIndex >= 3) return 4;
  if (maxWeekIndex >= 2) return 3;
  if (maxWeekIndex >= 1) return 2;

  return 1;
}

/**
 * Per-weekday map of shift unit IDs that are open for employee self-application.
 * Maps dayKey → shiftUnitId[].
 *
 * @example
 * {
 *   "weekday:0": ["unit-1", "unit-3"],
 *   "w1:weekday:2": ["unit-2"]
 * }
 */
export type TemplateApplicationOpenUnits = Record<string, string[]>;

/**
 * Full `__meta` structure for plan version data.
 */
export type TemplateDefaultSuppressions = Record<string, string[]>;

export interface TemplateMeta {
  templateSpanWeeks: TemplateSpanWeeks;
  capacityOverrides?: TemplateCapacityOverrides;
  applicationOpenUnits?: TemplateApplicationOpenUnits;
  defaultSuppressions?: TemplateDefaultSuppressions;
}

/**
 * Creates the `__meta` object for a given span and optional capacity/application-open overrides.
 */
export function createTemplateMeta(
  spanWeeks: TemplateSpanWeeks,
  capacityOverrides?: TemplateCapacityOverrides,
  applicationOpenUnits?: TemplateApplicationOpenUnits,
  defaultSuppressions?: TemplateDefaultSuppressions
): { __meta: TemplateMeta } {
  const meta: TemplateMeta = { templateSpanWeeks: spanWeeks };
  if (capacityOverrides && Object.keys(capacityOverrides).length > 0) {
    meta.capacityOverrides = capacityOverrides;
  }
  if (applicationOpenUnits && Object.keys(applicationOpenUnits).length > 0) {
    meta.applicationOpenUnits = applicationOpenUnits;
  }
  if (defaultSuppressions && Object.keys(defaultSuppressions).length > 0) {
    meta.defaultSuppressions = defaultSuppressions;
  }
  return { __meta: meta };
}

/**
 * Extracts `__meta.defaultSuppressions` from version data.
 * Returns an empty object when absent.
 */
export function getTemplateDefaultSuppressions(data: Record<string, unknown>): TemplateDefaultSuppressions {
  const meta = data['__meta'];
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const metaRecord = meta as Record<string, unknown>;
    if ('defaultSuppressions' in metaRecord) {
      const suppressions = metaRecord['defaultSuppressions'];
      if (suppressions && typeof suppressions === 'object' && !Array.isArray(suppressions)) {
        return suppressions as TemplateDefaultSuppressions;
      }
    }
  }
  return {};
}

/**
 * Extracts `__meta.capacityOverrides` from version data.
 * Returns an empty object when absent (backward-compatible default).
 */
export function getTemplateCapacityOverrides(data: Record<string, unknown>): TemplateCapacityOverrides {
  const meta = data['__meta'];
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const metaRecord = meta as Record<string, unknown>;
    if ('capacityOverrides' in metaRecord) {
      const overrides = metaRecord['capacityOverrides'];
      if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
        return overrides as TemplateCapacityOverrides;
      }
    }
  }
  return {};
}

/**
 * Extracts `__meta.applicationOpenUnits` from version data.
 * Returns an empty object when absent.
 */
export function getTemplateApplicationOpenUnits(data: Record<string, unknown>): TemplateApplicationOpenUnits {
  const meta = data['__meta'];
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const metaRecord = meta as Record<string, unknown>;
    if ('applicationOpenUnits' in metaRecord) {
      const units = metaRecord['applicationOpenUnits'];
      if (units && typeof units === 'object' && !Array.isArray(units)) {
        return units as TemplateApplicationOpenUnits;
      }
    }
  }
  return {};
}

/**
 * Checks whether a shift unit is open for employee applications on a specific date.
 *
 * Resolution order (instance override wins):
 * 1. If the instance override has `applicationOpen` set for this date + shiftUnitId, use that value.
 * 2. Otherwise, check the template-level `applicationOpenUnits` for the weekday pattern.
 * 3. Default: false (not open).
 *
 * @param templateOpenUnits - Template-level applicationOpenUnits from `__meta`
 * @param instanceOverrides - Instance-level overrides (date → shiftUnitId → override)
 * @param dateKey - ISO date string (YYYY-MM-DD)
 * @param dayKey - Weekday key (e.g. "weekday:0" or "w0:weekday:0")
 * @param shiftUnitId - The shift unit ID to check
 */
export function isShiftUnitApplicationOpen(
  templateOpenUnits: TemplateApplicationOpenUnits,
  instanceOverrides: Record<string, Record<string, { applicationOpen?: boolean }>> | null | undefined,
  dateKey: string,
  dayKey: string,
  shiftUnitId: string
): boolean {
  // 1. Instance override takes precedence
  const dateOverride = instanceOverrides?.[dateKey];
  if (dateOverride) {
    const unitOverride = dateOverride[shiftUnitId];
    if (unitOverride && unitOverride.applicationOpen !== undefined) {
      return unitOverride.applicationOpen;
    }
  }

  // 2. Template-level default
  const templateUnits = templateOpenUnits[dayKey];
  if (templateUnits && templateUnits.includes(shiftUnitId)) {
    return true;
  }

  // Also check normalized day key for legacy format compatibility
  const normalized = normalizeDayKey(dayKey);
  if (normalized && normalized !== dayKey) {
    const normalizedUnits = templateOpenUnits[normalized];
    if (normalizedUnits && normalizedUnits.includes(shiftUnitId)) {
      return true;
    }
  }

  return false;
}

/**
 * Resolves the effective capacity for a shift unit on a specific day key,
 * consulting template capacity overrides first, falling back to the default.
 *
 * @param overrides - Template capacity overrides from `__meta`
 * @param dayKey - The day key (e.g. "weekday:0" or "w1:weekday:3")
 * @param shiftUnitId - The shift unit ID
 * @param defaultCapacity - The shift template's default capacity
 * @returns The effective capacity for this unit on this day
 */
export function resolveTemplateCapacity(
  overrides: TemplateCapacityOverrides,
  dayKey: string,
  shiftUnitId: string,
  defaultCapacity: number
): number {
  const dayOverrides = overrides[dayKey];
  if (dayOverrides && shiftUnitId in dayOverrides) {
    return dayOverrides[shiftUnitId];
  }
  return defaultCapacity;
}

// ---------------------------------------------------------------------------
// Span / key consistency validation
// ---------------------------------------------------------------------------

/**
 * Validates that all day keys in `data` are consistent with the declared
 * `templateSpanWeeks`.
 *
 * Rules:
 * - `templateSpanWeeks=1` → only `w0:*` or legacy `weekday:*`
 * - `templateSpanWeeks=2` → max `w1:*`
 * - `templateSpanWeeks=3` → max `w2:*`
 * - `templateSpanWeeks=4` → max `w3:*`
 *
 * @returns Object with `isValid` flag and array of violating keys.
 */
export function validateSpanKeyConsistency(data: Record<string, unknown>): {
  isValid: boolean;
  invalidKeys: string[];
} {
  const spanWeeks = getTemplateSpanWeeks(data);
  const maxWeekIndex = spanWeeks - 1;
  const invalidKeys: string[] = [];

  for (const key of Object.keys(data)) {
    if (KNOWN_METADATA_KEYS.has(key)) continue;
    const parsed = parseDayKey(key);
    if (!parsed) continue; // non-day keys are checked by validatePlanVersionDataKeys
    if (parsed.weekIndex > maxWeekIndex) {
      invalidKeys.push(key);
    }
  }

  return { isValid: invalidKeys.length === 0, invalidKeys };
}

/**
 * Validates plan version data keys.
 *
 * ArkType's regex-keyed schema silently ignores non-matching keys, which can
 * lead to data loss. This function explicitly validates that all keys match
 * a recognised day-key pattern (legacy `weekday:N` or multi-week
 * `w{index}:weekday:N`) or a known metadata key, and returns errors for
 * invalid keys.
 *
 * @param data - The plan version data to validate
 * @returns Object with isValid flag and array of invalid keys
 */
export function validatePlanVersionDataKeys(data: unknown): {
  isValid: boolean;
  invalidKeys: string[];
} {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { isValid: true, invalidKeys: [] };
  }

  const keys = Object.keys(data as Record<string, unknown>);
  const invalidKeys = keys.filter(key => !DAY_KEY_PATTERN.test(key) && !KNOWN_METADATA_KEYS.has(key));

  return {
    isValid: invalidKeys.length === 0,
    invalidKeys,
  };
}

/**
 * Runtime-friendly type for plan version data.
 * This allows indexing with string keys while maintaining semantic meaning.
 */
export type PlanVersionData = Record<string, Record<string, string[]>>;

/**
 * Strips metadata keys (e.g. `shiftUnitIds`) from plan version data,
 * returning only the assignment entries (weekday-keyed and legacy date-keyed).
 *
 * Version data stored in JSONB contains both assignments and metadata keys.
 * Code that iterates `Object.values(data)` expecting only `Record<string, string[]>`
 * will break on metadata values (which are flat arrays).
 * Use this function before passing version data to processing pipelines.
 */
export function stripVersionMetadata(data: PlanVersionData | Record<string, unknown>): PlanVersionData {
  const result: PlanVersionData = {};
  for (const key of Object.keys(data)) {
    if (!KNOWN_METADATA_KEYS.has(key)) {
      result[key] = data[key] as Record<string, string[]>;
    }
  }
  return result;
}

/**
 * Extracts metadata keys from version data so they can be restored after processing.
 * Use this before `stripVersionMetadata` to capture metadata, then merge it back
 * into processed data before writing to the database.
 */
export function extractVersionMetadata(data: Record<string, unknown>): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  for (const key of KNOWN_METADATA_KEYS) {
    if (key in data) {
      metadata[key] = data[key];
    }
  }
  return metadata;
}

/**
 * Validation result for plan version data reference checking.
 */
export interface PlanVersionDataValidationResult {
  isValid: boolean;
  invalidEmployeeIds: string[];
  invalidShiftUnitIds: string[];
  errors: string[];
}
